<template>
    <app-layout title="Orders">

        <page-header>
            <template #title>Orders - {{ activeCustomer.name }}</template>
        </page-header>

        <div class="px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 flex-wrap" @submit.prevent="handleSubmit">
                    <!-- Search / Filters -->
                    <div class="flex flex-1 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="filter_by" value="Filter By" class="whitespace-nowrap"/>
                            <multiselect
                                v-model="form.filter_by"
                                :options="filterOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                :classes="{singleLabel: 'whitespace-nowrap w-full px-2'}"
                                class="multi-sm h-7 text-base"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleSubmit"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart="0"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label value="Shipping Method" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.shipping_method"
                                :options="shippingOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="status" value="Status" />
                            <multiselect
                                v-model="form.status"
                                :options="statusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                class="multi-sm h-7 text-base"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="search" value="Search" />
                            <jet-input type="text" class="h-7 text-base" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>
                        </div>
                    </div>
                </form>

                <div class="self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 pt-2 pb-4">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="text-left">Order #</th>
                            <th class="text-left">Order Date</th>
                            <th class="text-left">Ship Date</th>
                            <th class="text-left">Shipping Method</th>
                            <th class="text-left">Status</th>
                            <th class="text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="order in orders.data" :key="order.id">
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-auto" :href="route('customer_orders.show', order.id)">
                                    {{ order.id }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('customer_orders.show', order.id)">
                                    {{ formatDate(order.order_date) }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('customer_orders.show', order.id)">
                                    {{ formatDate(order.ship_date) }}
                                </Link>
                            </td>
                             <td class="text-left">
                                <Link class="block px-2 py-1 capitalize" :href="route('customer_orders.show', order.id)">
                                    {{ order.shipping_method }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1" :href="route('customer_orders.show', order.id)">
                                    {{ order.statusDef }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('customer_orders.show', order.id)">
                                    {{ toCurrency(total(order)) }}
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <pagination class="inline-flex" :links="orders.links" />
        </div>
    </app-layout>
</template>

<script>
import { useForm, Link } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import Multiselect from '@vueform/multiselect';
import { DateTime } from "luxon";
import Pagination from '@/Jetstream/Pagination';
import Datepicker from '@vuepic/vue-datepicker';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';
import { computeOrderTotals } from '@/Composables/useOrderTotals';

export default {
    setup(props) {
        const form = useForm({
            search: props.filters.search,
            status: props.filters.status,
            shipping_method: props.filters.shipping_method,
            filter_by: props.filters.filter_by ? props.filters.filter_by : 'ship_date',
            from: props.filters.from,
            to: props.filters.to,
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetLabel,
        JetTertiaryButton,
        Link,
        Multiselect,
        Pagination,
        Datepicker,
    },

    props: ['orders', 'filters'],

    data() {
        return {
            date: null,
            filterOptions: [
                { value:'order_date', label:'Order Date' },
                { value:'ship_date', label:'Ship Date' },
            ],
            statusOptions: [
                { value: -1, label: 'Cancelled' },
                { value: 0, label: 'New' },
                { value: 1, label: 'In Progress' },
                { value: 2, label: 'Invoiced' },
            ],
            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form
                .transform((data) => ({
                    ...data,
                    from: this.date ? DateTime.fromJSDate(this.date[0]).toISODate() : null,
                    to: this.date ? DateTime.fromJSDate(this.date[1]).toISODate() : null,
                }))
                .get(route('customer_orders.index'), { preserveState: true });
        }, 250),

        handleReset () {
            this.date = null;
            this.form.filter_by = 'ship_date';
            this.form.from = null;
            this.form.to = null;
            this.form.status = null;
            this.form.search = '';
            this.form.shipping_method = null;
            this.handleSubmit();
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        formatDate (date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        subtotal (order) {
            return computeOrderTotals(order).subtotal;
        },

        tax (order) {
            return computeOrderTotals(order).tax;
        },

        total (order) {
            return computeOrderTotals(order).total;
        },

        delivery_fee (order) {
            return computeOrderTotals(order).deliveryFeeAfterDiscount;
        },


    },

    computed: {
        activeCustomer() {
            return this.$page.props.activeCustomer;
        }
    },

    mounted() {
        this.setDateRange();
    }
}
</script>

<style scoped>

</style>

<style>
.dp-custom-input {
    @apply h-7;
}
</style>
